import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Plane, Plus, Edit2, Trash2, Search, User, Wrench,
  MapPin, Loader, CheckCircle, AlertCircle, X
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const EMPTY_FORM = {
  owner_id:              '',
  tail_number:           '',
  aircraft_type:         '',
  manufacturer:          '',
  model:                 '',
  year:                  '',
  year_manufactured:     '',
  serial_number:         '',
  callsign:              '',
  icao_code:             '',
  engine_type:           '',
  engine_manufacturer:   '',
  engine_model:          '',
  seats:                 '',
  fuel_type:             'JetA',
  fuel_capacity_liters:  '',
  mtow_kg:               '',
  home_base:             '',
  parking_spot:          '',
  owner_name:            '',
  owner_address:         '',
  airworthiness_date:    '',
  certificate_issue_date:'',
};

const AircraftView = () => {
  const [aircraft, setAircraft]           = useState([]);
  const [clients, setClients]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showModal, setShowModal]         = useState(false);
  const [editingAircraft, setEditingAircraft] = useState(null);
  const [searchTerm, setSearchTerm]       = useState('');
  const [formData, setFormData]           = useState(EMPTY_FORM);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState(null);

  // Lookup state
  const [lookupStatus, setLookupStatus]   = useState(null);
  const lookupTimer                       = useRef(null);

  /* ── Fetch ──────────────────────────────────────────────── */
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [aircraftRes, clientsRes] = await Promise.all([
        axios.get(`${API_BASE}/aircraft/`),
        axios.get(`${API_BASE}/owners/`),
      ]);
      setAircraft(Array.isArray(aircraftRes.data) ? aircraftRes.data : []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
    } catch (err) {
      setError('Unable to load data. Please check that the server is running.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  /* ── Auto-lookup on tail number ─────────────────────────── */
  const handleTailNumberChange = (value) => {
    const tail = value.toUpperCase();
    field('tail_number', tail);
    setLookupStatus(null);

    if (tail.length < 4) return;

    clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(() => triggerLookup(tail), 800);
  };

  const triggerLookup = async (tail) => {
    setLookupStatus('loading');
    try {
      const res = await axios.get(`${API_BASE}/lookup/${tail}`);
      const data = res.data;
      
      console.log('Lookup data received:', data);

      setFormData(prev => ({
        ...prev,
        manufacturer:          data.manufacturer      || prev.manufacturer,
        model:                 data.model             || prev.model,
        year_manufactured:     data.year_manufactured || prev.year_manufactured,
        serial_number:         data.serial_number     || prev.serial_number,
        engine_type:           data.engine_type       || prev.engine_type,
        icao_code:             data.icao_code         || prev.icao_code,
        owner_name:            data.owner_name        || prev.owner_name,
        engine_manufacturer:   data.engine_manufacturer || prev.engine_manufacturer,
        engine_model:          data.engine_model      || prev.engine_model,
        airworthiness_date:    data.airworthiness_date || prev.airworthiness_date,
      }));
      setLookupStatus('found');
    } catch (err) {
      console.error('Lookup error:', err);
      if (err.response?.status === 404) {
        setLookupStatus('notfound');
      } else {
        setLookupStatus('error');
      }
    }
  };

  /* ── CRUD ───────────────────────────────────────────────── */
  const handleSubmit = async () => {
    if (!formData.tail_number || !formData.owner_id) {
      alert('Tail number and owner are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        year:                  formData.year                  ? parseInt(formData.year)                  : undefined,
        year_manufactured:     formData.year_manufactured     ? parseInt(formData.year_manufactured)     : undefined,
        seats:                 formData.seats                 ? parseInt(formData.seats)                 : undefined,
        fuel_capacity_liters:  formData.fuel_capacity_liters  ? parseInt(formData.fuel_capacity_liters)  : undefined,
        mtow_kg:               formData.mtow_kg               ? parseInt(formData.mtow_kg)               : undefined,
        airworthiness_date:    formData.airworthiness_date    || undefined,
        certificate_issue_date:formData.certificate_issue_date|| undefined,
      };

      if (editingAircraft) {
        await axios.put(`${API_BASE}/aircraft/${editingAircraft.id}`, payload);
      } else {
        await axios.post(`${API_BASE}/aircraft`, payload);
      }
      await fetchData();
      closeModal();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error saving data');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ac) => {
    if (!window.confirm(`Delete aircraft ${ac.tail_number}?`)) return;
    try {
      await axios.delete(`${API_BASE}/aircraft/${ac.id}`);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting aircraft');
    }
  };

  /* ── Modal helpers ──────────────────────────────────────── */
  const openCreateModal = () => {
    setEditingAircraft(null);
    setFormData(EMPTY_FORM);
    setLookupStatus(null);
    setShowModal(true);
  };

  const openEditModal = (ac) => {
    setEditingAircraft(ac);
    setFormData({
      owner_id:               ac.owner_id               ?? '',
      tail_number:            ac.tail_number             ?? '',
      aircraft_type:          ac.aircraft_type           ?? '',
      manufacturer:           ac.manufacturer            ?? '',
      model:                  ac.model                  ?? '',
      year:                   ac.year                   ?? '',
      year_manufactured:      ac.year_manufactured       ?? '',
      serial_number:          ac.serial_number           ?? '',
      callsign:               ac.callsign               ?? '',
      icao_code:              ac.icao_code              ?? '',
      engine_type:            ac.engine_type            ?? '',
      engine_manufacturer:    ac.engine_manufacturer    ?? '',
      engine_model:           ac.engine_model           ?? '',
      seats:                  ac.seats                  ?? '',
      fuel_type:              ac.fuel_type              ?? 'JetA',
      fuel_capacity_liters:   ac.fuel_capacity_liters   ?? '',
      mtow_kg:                ac.mtow_kg                ?? '',
      home_base:              ac.home_base              ?? '',
      parking_spot:           ac.parking_spot           ?? '',
      owner_name:             ac.owner_name             ?? '',
      owner_address:          ac.owner_address          ?? '',
      airworthiness_date:     ac.airworthiness_date     ?? '',
      certificate_issue_date: ac.certificate_issue_date ?? '',
    });
    setLookupStatus(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAircraft(null);
    setFormData(EMPTY_FORM);
    setLookupStatus(null);
    clearTimeout(lookupTimer.current);
  };

  const field = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  /* ── Filter ─────────────────────────────────────────────── */
  const filtered = aircraft.filter(a =>
    a.tail_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.aircraft_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.owner_company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* ── Lookup badge ───────────────────────────────────────── */
  const LookupBadge = () => {
    if (!lookupStatus) return null;
    const map = {
      loading:  { icon: <Loader size={14} className="animate-spin" />, text: 'Searching FAA…',          color: 'text-blue-400'  },
      found:    { icon: <CheckCircle size={14} />,                      text: 'FAA data imported',     color: 'text-green-400' },
      notfound: { icon: <AlertCircle size={14} />,                      text: 'Not found — manual entry', color: 'text-yellow-400' },
      error:    { icon: <AlertCircle size={14} />,                      text: 'Lookup error',           color: 'text-red-400'   },
    };
    const { icon, text, color } = map[lookupStatus];
    return (
      <div className={`flex items-center gap-1 text-xs mt-1 ${color}`}>
        {icon} {text}
      </div>
    );
  };

  /* ── Render ─────────────────────────────────────────────── */
  if (loading) return (
    <div className="flex items-center justify-center py-20 text-gray-400">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3" />
      Loading…
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 px-4">
      <p className="text-red-400 text-center">{error}</p>
      <button onClick={fetchData} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Retry</button>
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6 px-2 sm:px-0">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white">Aircraft Management</h2>
          <p className="text-gray-400 text-xs md:text-sm mt-1">Aircraft registered at KBOE · {aircraft.length} total</p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg flex items-center gap-2 transition text-sm sm:text-base w-full sm:w-auto justify-center"
        >
          <Plus size={18} /> New aircraft
        </button>
      </div>

      {/* Search - Full width on mobile */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Search by tail number, type or owner…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white text-sm md:text-base"
        />
      </div>

      {/* Grid - Responsive: 1 colonne mobile, 2 tablettes, 3 desktop */}
      {filtered.length === 0
        ? <p className="text-center text-gray-500 py-16">No aircraft found.</p>
        : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {filtered.map(ac => (
              <div key={ac.id} className="bg-gray-800/50 rounded-xl border border-gray-700 p-3 md:p-4 hover:border-gray-600 transition">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Plane className="text-blue-400 flex-shrink-0" size={20} />
                    <div className="min-w-0">
                      <h3 className="text-white font-bold text-sm md:text-base truncate">{ac.tail_number}</h3>
                      <p className="text-gray-400 text-xs truncate">{ac.aircraft_type || `${ac.manufacturer || ''} ${ac.model || ''}`.trim() || '—'}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => openEditModal(ac)} className="text-blue-400 hover:text-blue-300 transition p-1"><Edit2 size={16} /></button>
                    <button onClick={() => handleDelete(ac)} className="text-red-400 hover:text-red-300 transition p-1"><Trash2 size={16} /></button>
                  </div>
                </div>
                <div className="space-y-1 text-xs md:text-sm">
                  <div className="flex items-center gap-2 text-gray-300">
                    <User size={14} className="text-gray-500 flex-shrink-0" />
                    <span className="truncate">{ac.owner_name || ac.owner_company || 'Unknown owner'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <Wrench size={14} className="text-gray-500 flex-shrink-0" />
                    <span className="truncate">{[ac.manufacturer, ac.model].filter(Boolean).join(' ') || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300 flex-wrap">
                    <MapPin size={14} className="text-gray-500 flex-shrink-0" />
                    <span className="truncate">Fuel: {ac.fuel_type || '—'}{ac.parking_spot ? ` · Spot: ${ac.parking_spot}` : ''}</span>
                  </div>
                  {ac.year_manufactured && (
                    <div className="text-xs text-gray-500">Year: {ac.year_manufactured}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      }

      {/* ── Modal ── RESPONSIVE */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-[95vw] sm:max-w-2xl shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[90vh]">

            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-gray-700 flex justify-between items-center">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  {editingAircraft ? `Edit ${editingAircraft.tail_number}` : 'New aircraft'}
                </h3>
                {!editingAircraft && (
                  <p className="text-gray-400 text-xs mt-1 hidden sm:block">
                    Enter the tail number — FAA data loads automatically.
                  </p>
                )}
              </div>
              <button onClick={closeModal} className="text-gray-400 hover:text-white transition p-1"><X size={20} /></button>
            </div>

            {/* Body - Scrollable */}
            <div className="p-4 sm:p-6 overflow-y-auto space-y-4 sm:space-y-5 flex-1">
              {/* Section: Identification - Responsive grid */}
              <Section title="Identification">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label>Tail Number *</Label>
                    <input
                      type="text"
                      placeholder="ex: N12345"
                      value={formData.tail_number}
                      onChange={e => handleTailNumberChange(e.target.value)}
                      disabled={!!editingAircraft}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm disabled:opacity-50"
                    />
                    <LookupBadge />
                  </div>
                  <div>
                    <Label>Manufacturer</Label>
                    <input type="text" placeholder="ex: Cessna" value={formData.manufacturer}
                      onChange={e => field('manufacturer', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Model</Label>
                    <input type="text" placeholder="ex: 172 Skyhawk" value={formData.model}
                      onChange={e => field('model', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Aircraft Type</Label>
                    <input type="text" placeholder="ex: Single Engine Piston" value={formData.aircraft_type}
                      onChange={e => field('aircraft_type', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Year Manufactured</Label>
                    <input type="number" placeholder="ex: 2018" value={formData.year_manufactured}
                      onChange={e => field('year_manufactured', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Serial Number</Label>
                    <input type="text" value={formData.serial_number}
                      onChange={e => field('serial_number', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>ICAO Code</Label>
                    <input type="text" placeholder="ex: C172" value={formData.icao_code}
                      onChange={e => field('icao_code', e.target.value.toUpperCase())}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Callsign</Label>
                    <input type="text" value={formData.callsign}
                      onChange={e => field('callsign', e.target.value.toUpperCase())}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                </div>
              </Section>

              {/* Section: Engine & Fuel - Responsive */}
              <Section title="Engine & Fuel">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Engine Type</Label>
                    <input type="text" placeholder="ex: Piston" value={formData.engine_type}
                      onChange={e => field('engine_type', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Engine Manufacturer</Label>
                    <input type="text" placeholder="ex: Lycoming" value={formData.engine_manufacturer}
                      onChange={e => field('engine_manufacturer', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Engine Model</Label>
                    <input type="text" value={formData.engine_model}
                      onChange={e => field('engine_model', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Seats</Label>
                    <input type="number" min="1" value={formData.seats}
                      onChange={e => field('seats', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Fuel Type</Label>
                    <select value={formData.fuel_type} onChange={e => field('fuel_type', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                      <option value="100LL">100LL (Avgas)</option>
                      <option value="JetA">Jet-A</option>
                      <option value="JetA_plus">Jet-A Plus</option>
                    </select>
                  </div>
                  <div>
                    <Label>Fuel Capacity (L)</Label>
                    <input type="number" min="0" value={formData.fuel_capacity_liters}
                      onChange={e => field('fuel_capacity_liters', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>MTOW (kg)</Label>
                    <input type="number" min="0" value={formData.mtow_kg}
                      onChange={e => field('mtow_kg', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                </div>
              </Section>

              {/* Section: Owner & Position */}
              <Section title="Owner & Position">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label>Owner (account) *</Label>
                    <select value={formData.owner_id} onChange={e => field('owner_id', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                      <option value="">— Select an owner —</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.company_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || c.email}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Owner Name (registry)</Label>
                    <input type="text" value={formData.owner_name}
                      onChange={e => field('owner_name', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Home Base</Label>
                    <input type="text" placeholder="ex: KBOE" value={formData.home_base}
                      onChange={e => field('home_base', e.target.value.toUpperCase())}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Owner Address (registry)</Label>
                    <textarea rows={2} value={formData.owner_address}
                      onChange={e => field('owner_address', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Parking Spot</Label>
                    <input type="text" value={formData.parking_spot}
                      onChange={e => field('parking_spot', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                </div>
              </Section>

              {/* Section: Certificates */}
              <Section title="Certificates">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Airworthiness Date</Label>
                    <input type="date" value={formData.airworthiness_date}
                      onChange={e => field('airworthiness_date', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                  <div>
                    <Label>Certificate Issue Date</Label>
                    <input type="date" value={formData.certificate_issue_date}
                      onChange={e => field('certificate_issue_date', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                  </div>
                </div>
              </Section>

            </div>

            {/* Footer - Boutons responsive (colonne sur mobile) */}
            <div className="p-4 sm:p-6 border-t border-gray-700 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <button onClick={closeModal}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition w-full sm:w-auto">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !formData.tail_number || !formData.owner_id}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition w-full sm:w-auto"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Small helpers (inchangés) ───────────────────────────────────────── */
const Section = ({ title, children }) => (
  <div>
    <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-3 border-b border-gray-700 pb-1">
      {title}
    </h4>
    {children}
  </div>
);

const Label = ({ children }) => (
  <label className="text-gray-400 text-xs mb-1 block">{children}</label>
);

export default AircraftView;